import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { Slider, Rail, Handles, Ticks } from "react-compound-slider";
import useStyles from "./SliderRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { LabelPosition } from "../../../models/enums/LabelPosition";
import { RecalledText } from "../../../containers/shared/RecalledText";
import useTranslatable from "../../../hooks/useTranslatable";
import Button from "../../shared/Button";
import { isNullOrEmpty } from "../../../utils";
import { Translatable } from "../../../models/Translatable";
import { ElementWithSettings } from "../../../models/ElementWithSettings";
import { ButtonChoice } from "../../shared/ButtonChoice";
import { CheckBoxIcon } from "../../shared/CheckBoxIcon";
import InputWrapper from "../../shared/InputWrapper";
import useQuestion from "../../../hooks/useQuestion";
import { size } from "../../../models/utilities/questionUtilities";

interface Props extends RendererProps {
    value: number;
    domain: number[];
    stepValue: number;
    hasValueSet: boolean;
    hasNoAnswer: boolean;
    noAnswerLabel: string;
    decimals?: number;
    displayTicks: boolean;
    tickValues?: number[];
    onChange?(value: number[]): void;
    onClearAnswer?(): void;
    onToggleNoAnswer?(): void;
    displayClearButton: boolean;
    displayNoAnswer?: boolean;
    isDiscrete: boolean;
    discreteValues?: { [value: number]: DiscreteValue };
    stepDecimalPlaces: number;
    tickDecimalPlaces: number;
    defaultPosition?: number;
    hasImages?: boolean;
    isHybridGrid?: boolean;
    elementId: string;
    isDisabled?: boolean;
}

export interface DiscreteValue extends ElementWithSettings, Translatable {
    elementId: string;
    choiceValueCode: string;
}

function onChoiceKeyDown(event, onToggleNoAnswer) {
    if (event.target !== event.currentTarget) {
        return;
    }
    if (event.key === " ") {
        onToggleNoAnswer();
        event.preventDefault();
    }
}

function Handle({
    handle: { id, value, percent },
    domain,
    hasValueSet,
    getHandleProps,
    isDiscrete,
    discreteValues,
    decimalPlaces,
    defaultPosition,
    isHybridGrid,
    onChange,
}) {
    const [active, setActive] = React.useState(false);
    const [min, max] = domain;

    const theme = useTheme();
    const classes = useStyles({
        theme,
        percent: hasValueSet ? percent : defaultPosition,
        isHybridGrid,
    });
    const onFocus = () => {
        if (!hasValueSet) {
            onChange([value]);
        }
        setActive(true);
    };

    return (
        <div
            className={classnames(classes.handle, active && classes.handleActive)}
            {...getHandleProps(id)}
            tabIndex={0}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={hasValueSet ? value.toFixed(decimalPlaces) : undefined}
            onBlur={() => setActive(false)}
            onMouseOut={() => setActive(false)}
        >
            {hasValueSet && (
                <div
                    onFocus={onFocus}
                    onMouseOver={() => setActive(true)}
                    className={classnames(
                        classes.handleLabelContainer,
                        isHybridGrid && !isDiscrete
                            ? classes.handleLabelContainerHybridGrid
                            : classes.handleLabelContainerRegular,
                    )}
                >
                    <HandleLabel
                        discreteValues={discreteValues}
                        value={value}
                        isHybridGrid={isHybridGrid}
                        decimalPlaces={decimalPlaces}
                        isDiscrete={isDiscrete}
                    />
                </div>
            )}
        </div>
    );
}

function HandleLabel({ discreteValues, value, isHybridGrid, decimalPlaces, isDiscrete }) {
    const imageUrl = useTranslatable(discreteValues && discreteValues[value], "ImageUrl");
    if (!isNullOrEmpty(imageUrl)) {
        return (
            <DiscreteHandleLabelWithImage
                value={discreteValues[value]}
                isHybridGrid={isHybridGrid}
            />
        );
    }
    if (isDiscrete) {
        return <DiscreteHandleLabel value={discreteValues[value]} isHybridGrid={isHybridGrid} />;
    }
    return (
        <StandardHandleLabel
            value={value}
            decimalPlaces={decimalPlaces}
            isHybridGrid={isHybridGrid}
        />
    );
}

function StandardHandleLabel({ value, decimalPlaces, isHybridGrid }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const displayValue = value.toFixed(decimalPlaces);
    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow />}
            <div
                className={classnames(
                    classes.handleLabelText,
                    isHybridGrid
                        ? classes.handleLabelTextHybridGrid
                        : classes.handleLabelTextRegular,
                )}
            >
                {displayValue}
            </div>
            {displayValue} {/* This is a placeholder to calculate the container size */}
        </>
    );
}

function DiscreteHandleLabel({ value, isHybridGrid }) {
    const alt = useTranslatable(value, "TEXT") || value.choiceValueCode;
    const theme = useTheme();
    const classes = useStyles({ theme, isDiscrete: true });
    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow isDiscrete />}
            <div
                className={classnames(classes.handleLabelText, classes.handleLabelTextRegular)}
                title={alt}
            >
                <div className={classes.handleLabelTextDiscrete}>{alt}</div>
            </div>
            {alt}
        </>
    );
}

function DiscreteHandleLabelWithImage({ value, isHybridGrid }) {
    const imageUrl = useTranslatable(value, "ImageUrl");
    const alt = useTranslatable(value, "TEXT") || value.choiceValueCode;
    const theme = useTheme();
    const classes = useStyles({ theme, imageUrl, isDiscrete: true });
    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow isDiscrete />}
            <div
                className={classnames(
                    classes.handleLabelImageContainer,
                    classes.handleLabelTextRegular,
                )}
                title={alt}
            >
                <div className={classes.handleLabelImage} />
                <div className={classes.handleLabelTextDiscrete}>{alt}</div>
            </div>
            {alt.length < 10 ? (
                <div>{alt}</div>
            ) : (
                <div>{alt.concat(alt).substring(0, (alt.length * 2) / 1.6)}</div>
            )}
        </>
    );
}

function HandleLabelArrow({ isDiscrete }: { isDiscrete?: boolean }) {
    const theme = useTheme();
    const classes = useStyles({ theme, isDiscrete });
    return (
        <>
            <div className={classes.handleLabelArrowBorder} />
            <div className={classes.handleLabelArrow} />
        </>
    );
}

function Tick({ tick, onChange, count, isDiscrete, discreteValues, decimalPlaces, isHybridGrid }) {
    const { percent, value } = tick;
    const theme = useTheme();
    const classes = useStyles({ theme, percent, count });

    const id = isDiscrete ? discreteValues[value]?.elementId : null;
    const discreteValue = isDiscrete ? discreteValues[value] : undefined;
    const discreteValueText =
        useTranslatable(discreteValue, "TEXT") ||
        (discreteValue !== undefined && discreteValue.choiceValueCode) ||
        value;
    const displayValue = isDiscrete ? discreteValueText : value.toFixed(decimalPlaces);

    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div onClick={() => onChange([value])}>
                <div className={classes.tick} />
                <div
                    id={id}
                    className={classnames(
                        classes.tickLabel,
                        isHybridGrid ? classes.tickLabelHybridGrid : classes.tickLabelRegular,
                    )}
                    dangerouslySetInnerHTML={{ __html: displayValue }}
                />
            </div>
        </>
    );
}

function Label({ text, position }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const positionalClasses = {
        [LabelPosition.Left]: classes.labelLeft,
        [LabelPosition.Middle]: classes.labelMiddle,
        [LabelPosition.Right]: classes.labelRight,
    };
    if (isNullOrEmpty(text)) {
        return null;
    }
    return (
        <RecalledText
            text={text}
            render={(recalled) => (
                <div
                    className={classnames(classes.label, positionalClasses[position])}
                    dangerouslySetInnerHTML={{ __html: recalled }}
                />
            )}
        />
    );
}

function ClearButton({ theme, question, onClearAnswer, isDisabled = false }) {
    const defaultText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const text = useTranslatable(question, "ResetButtonText") || defaultText;

    return (
        <Button
            className={`slider__button--clear ${isDisabled && "disabled"}`}
            text={text}
            size="full"
            onClick={() => onClearAnswer()}
        />
    );
}

function CompoundSlider(props: Props) {
    const {
        value,
        domain,
        hasValueSet,
        stepValue,
        onChange,
        displayTicks,
        tickValues,
        isDiscrete,
        discreteValues,
        stepDecimalPlaces,
        tickDecimalPlaces,
        defaultPosition,
        hasImages,
        isHybridGrid,
        elementId,
        isDisabled,
    } = props;
    const question = useQuestion();
    const sliderSize = isHybridGrid ? size(question) : "Full";
    const theme = useTheme();
    const classes = useStyles({ theme, size: sliderSize, hasImages, isHybridGrid });

    return (
        <div
            id={elementId}
            className={`${classes.sliderContainer} ${isDisabled && classes.disabled}`}
        >
            <Slider
                className={classes.slider}
                mode={1}
                domain={domain}
                step={stepValue}
                values={[value]}
                onUpdate={value || isDisabled ? undefined : onChange}
                onChange={isDisabled ? undefined : onChange}
                warnOnChanges={false}
                disabled={isDisabled === true}
            >
                <Rail>
                    {({ getRailProps }) => (
                        <div
                            className={`${classes.rail} ${isDisabled && classes.disabled}`}
                            {...getRailProps()}
                        />
                    )}
                </Rail>
                <Handles>
                    {({ handles, getHandleProps }) => (
                        <div className={classes.handleContainer}>
                            {handles.map((handle) => (
                                <Handle
                                    key={handle.id}
                                    handle={handle}
                                    hasValueSet={hasValueSet}
                                    getHandleProps={getHandleProps}
                                    isDiscrete={isDiscrete}
                                    discreteValues={discreteValues}
                                    decimalPlaces={stepDecimalPlaces}
                                    isHybridGrid={isHybridGrid}
                                    domain={domain}
                                    defaultPosition={defaultPosition}
                                    onChange={isDisabled ? undefined : onChange}
                                />
                            ))}
                        </div>
                    )}
                </Handles>
                {displayTicks && (
                    <Ticks values={tickValues}>
                        {({ ticks }) => (
                            <div className={classes.tickContainer}>
                                {ticks.map((tick) => (
                                    <Tick
                                        key={tick.id}
                                        tick={tick}
                                        count={ticks.length}
                                        isDiscrete={isDiscrete}
                                        onChange={onChange}
                                        discreteValues={discreteValues}
                                        decimalPlaces={tickDecimalPlaces}
                                        isHybridGrid={isHybridGrid}
                                    />
                                ))}
                            </div>
                        )}
                    </Ticks>
                )}
            </Slider>
        </div>
    );
}

function NoAnswer(props: Props) {
    const {
        displayNoAnswer,
        hasNoAnswer,
        onToggleNoAnswer,
        isHybridGrid,
        noAnswerLabel,
        isDisabled = false,
    } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });

    if (!displayNoAnswer) {
        return null;
    }

    if (isHybridGrid) {
        return (
            <div
                role="checkbox"
                aria-checked={hasNoAnswer}
                tabIndex={0}
                className={classes.noAnswer}
                onKeyDown={
                    isDisabled ? undefined : (event) => onChoiceKeyDown(event, onToggleNoAnswer)
                }
                onClick={isDisabled ? undefined : onToggleNoAnswer}
            >
                <CheckBoxIcon checked={hasNoAnswer} backgroundless disabled={isDisabled} />
                <span className={classes.noAnswerLabel}>{noAnswerLabel}</span>
            </div>
        );
    }

    return (
        <ButtonChoice
            type="checkbox"
            onKeyDown={isDisabled ? undefined : (event) => onChoiceKeyDown(event, onToggleNoAnswer)}
            onSelect={isDisabled ? undefined : onToggleNoAnswer}
            selected={hasNoAnswer}
            label={noAnswerLabel}
            className={classes.noAnswer}
        />
    );
}

export default function SliderRenderer(props: Props) {
    const {
        theme: surveyTheme,
        question,
        displayClearButton,
        onClearAnswer,
        isHybridGrid,
        isDisabled,
    } = props;
    const theme = useTheme();
    const classes = useStyles({ theme });

    const leftLabel = useTranslatable(question, "LeftLabel");
    const middleLabel = useTranslatable(question, "MiddleLabel");
    const rightLabel = useTranslatable(question, "RightLabel");

    const slider = (
        <>
            <CompoundSlider {...props} />
            <div className={`${classes.labelContainer} ${isDisabled && classes.disabled}`}>
                {!isHybridGrid && <Label text={leftLabel} position={LabelPosition.Left} />}
                <Label text={middleLabel} position={LabelPosition.Middle} />
                {!isHybridGrid && <Label text={rightLabel} position={LabelPosition.Right} />}
            </div>
            <NoAnswer {...props} />
            {displayClearButton && (
                <ClearButton
                    theme={surveyTheme}
                    question={question}
                    onClearAnswer={isDisabled ? undefined : onClearAnswer}
                    isDisabled
                />
            )}
        </>
    );

    if (isHybridGrid)
        return (
            <InputWrapper leftLabel={leftLabel} rightLabel={rightLabel}>
                {slider}
            </InputWrapper>
        );

    return slider;
}
